import { TranslationsPanel } from '@/components/TranslationsPanel';
import { TranslationWork } from '@/components/TranslationWork';
import { useQueryClient } from '@tanstack/react-query';
import { applyToolEffects } from '@/lib/mediaEffects';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef, useMemo } from 'react';
import { scheduledPublicationMessage, usePost, useUpdatePost, usePostTags, useSetPostTags, useSite, useActivityPubStatus, postsApi, type Post } from '@wordpuppi/shared';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { BaxterChangedBar } from '@/components/baxter/BaxterChangedBar';
import { openPreview } from '@/utils/desktopActions';
import { PostEditor } from '@/components/PostEditor';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { UnsavedGuard } from '@/components/UnsavedGuard';
import { EditorDiskBar } from '@/components/EditorDiskBar';
import type { PostFrontMatter } from '@/utils/frontmatter';
import { normalizeForEditor, normalizeForSave, isHtmlContent, htmlToMarkdown } from '@/utils/contentFormat';
import { TagSelector } from '@/components/TagSelector';
import { PostSeoPanel, emptySeoFields, type SeoFields } from '@/components/PostSeoPanel';
import { FeatureImageCard, emptyFeatureImage, type FeatureImageFields } from '@/components/FeatureImageCard';
import { GeoPanel } from '@/components/GeoPanel';
import { BarkPanel } from '@/components/BarkPanel';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { SaveSplitButton } from '@/components/SaveSplitButton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/sites/$siteSlug/posts/$postId')({
  component: EditPostPage,
});

function EditPostPage() {
  const { siteSlug, postId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: siteData } = useSite(siteSlug); // Cached from parent layout
  const { data, isLoading, error, refetch } = usePost(siteSlug, postId);
  const { data: postTagsData } = usePostTags(siteSlug, postId);
  const updatePost = useUpdatePost(siteSlug);
  const setPostTags = useSetPostTags(siteSlug);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<Post['status']>('draft');
  const [slug, setSlug] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [seo, setSeo] = useState<SeoFields>(emptySeoFields);
  const [featureImage, setFeatureImage] = useState<FeatureImageFields>(emptyFeatureImage);

  // Track if form has been initialized from server data
  // This prevents refetches from overwriting user edits
  const formInitialized = useRef(false);

  // MDXEditor reformats markdown on mount (e.g. `-` bullets -> `*`), firing onChange
  // with its canonical serialization before any user edit. We baseline the dirty check
  // on that FIRST settled emission, not the stored string, so a pristine load reads clean.
  const [settledContent, setSettledContent] = useState<string | null>(null);
  useEffect(() => {
    setSettledContent(null);
  }, [postId]);
  const handleContentChange = (value: string) => {
    setContent(value);
    if (formInitialized.current && settledContent === null) {
      setSettledContent(value);
    }
  };

  // Server data -> form. Shared by the initial load and by a Baxter reload, so
  // "put what's on the server into the fields" has exactly one definition.
  const applyServer = (p: Post) => {
    setTitle(p.title);
    setContent(normalizeForEditor(p.content || ''));
    setStatus(p.status);
    setSlug(p.slug);
    setSeo({
      seo_title: p.seo_title ?? '',
      seo_description: p.seo_description ?? '',
      excerpt: p.excerpt ?? '',
      noindex: p.noindex ?? false,
    });
    setFeatureImage({
      featured_image: p.featured_image ?? '',
      featured_image_alt: p.featured_image_alt ?? '',
      featured_image_caption: p.featured_image_caption ?? '',
    });
  };

  // Load post data into form (only on initial load)
  useEffect(() => {
    if (data?.data && !formInitialized.current) {
      applyServer(data.data);
      formInitialized.current = true;
    }
  }, [data]);

  // AB#675: Baxter edited THIS post — clean editor re-reads, dirty one keeps
  // every keystroke and gets the bar.
  const baxterAt = useBaxterChanged('posts', postId);
  const seenBaxter = useRef<number | null>(null);
  const [changedBar, setChangedBar] = useState(false);
  const reloadFromServer = async () => {
    const fresh = await refetch();
    const p = fresh.data?.data;
    if (!p) return;
    applyServer(p);
    setSettledContent(null); // re-baseline on what the server now holds
    setChangedBar(false);
  };

  // Load post tags
  useEffect(() => {
    if (postTagsData?.data) {
      setSelectedTagIds(postTagsData.data.map((tag) => tag.id));
    }
  }, [postTagsData]);

  // AB#430: dirty = current form values differ from what the server last gave us.
  // Baseline is derived from the query data (and refreshes after a save refetch), so
  // saving resets clean automatically. Content is compared with the same whitespace
  // normalization the editor sync uses, to avoid spurious dirtiness.
  const norm = (s: string) => s.replace(/\n{3,}/g, '\n\n').trim();
  const baseline = useMemo(() => {
    if (!data?.data) return null;
    return JSON.stringify({
      title: data.data.title,
      // settledContent is MDXEditor's own serialization of the loaded doc; fall back to
      // the stored string until the editor settles (e.g. before first onChange fires).
      content: norm(settledContent ?? normalizeForEditor(data.data.content || '')),
      status: data.data.status,
      slug: data.data.slug,
      tagIds: [...(postTagsData?.data?.map((t) => t.id) ?? [])].sort(),
      seo_title: data.data.seo_title ?? '',
      seo_description: data.data.seo_description ?? '',
      excerpt: data.data.excerpt ?? '',
      noindex: data.data.noindex ?? false,
      featured_image: data.data.featured_image ?? '',
      featured_image_alt: data.data.featured_image_alt ?? '',
      featured_image_caption: data.data.featured_image_caption ?? '',
    });
  }, [data, postTagsData, settledContent]);

  const current = JSON.stringify({
    title,
    content: norm(content),
    status,
    slug,
    tagIds: [...selectedTagIds].sort(),
    seo_title: seo.seo_title,
    seo_description: seo.seo_description,
    excerpt: seo.excerpt,
    noindex: seo.noindex,
    featured_image: featureImage.featured_image,
    featured_image_alt: featureImage.featured_image_alt,
    featured_image_caption: featureImage.featured_image_caption,
  });
  const dirty = baseline !== null && baseline !== current;

  useEffect(() => {
    if (baxterAt == null || baxterAt === seenBaxter.current) return;
    seenBaxter.current = baxterAt;
    if (dirty) setChangedBar(true);
    else void reloadFromServer();
  }, [baxterAt, dirty]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !data) {
    return <div className="text-center py-12 text-muted-foreground">Post not found</div>;
  }

  const doSave = async () => {
    try {
      const savedPost = await updatePost.mutateAsync({
        id: postId,
        data: {
          title,
          content: normalizeForSave(content),
          status,
          slug,
          seo_title: seo.seo_title || undefined,
          seo_description: seo.seo_description || undefined,
          excerpt: seo.excerpt || undefined,
          // Sent as '' (not undefined) so Remove actually clears the column —
          // an omitted key leaves the existing value in place.
          featured_image: featureImage.featured_image,
          featured_image_alt: featureImage.featured_image_alt,
          featured_image_caption: featureImage.featured_image_caption,
          noindex: seo.noindex,
        },
      });
      await setPostTags.mutateAsync({ postId, tagIds: selectedTagIds });
      // Re-baseline on what was just persisted — the mount-time snapshot is stale
      // now, and the refetched server value may serialize differently than the
      // editor's current doc (the same delta that caused the false-dirty on load).
      setSettledContent(content);
      void applyToolEffects(queryClient, siteSlug, { name: 'wpp_update_content', args: { site_slug: siteSlug, content_type: 'posts', id: postId, data: { title, content, featured_image: featureImage.featured_image } }, previousData: data.data }, { ok: true }).catch(() => {});
      toast.success(scheduledPublicationMessage(status, savedPost.meta?.scheduled_publication, data.data?.scheduled_for) ?? 'Post updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update post');
      throw err;
    }
  };

  const handleSave = async () => {
    await doSave();
  };

  const handleSaveAndClose = async () => {
    await doSave();
    navigate({ to: '/sites/$siteSlug/posts', params: { siteSlug } });
  };

  const handlePreview = async () => {
    const previewKey = `preview-${Date.now()}`;
    const data = { title, content, slug: slug || 'preview', ...featureImage };
    const base = import.meta.env.BASE_URL.replace(/\/$/, '');
    // AB#488/#530 (browser) + AB#533 (desktop): openPreview branches on isTauri.
    try {
      await openPreview({
        browserUrl: `${base}/sites/${siteSlug}/preview?type=post&key=${previewKey}`,
        storageKey: previewKey,
        storageValue: JSON.stringify(data),
        render: () => postsApi.previewFull(siteSlug, data),
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to open preview');
    }
  };

  // AB#426: front-matter for local disk save. Body is Markdown (the editor already
  // works in Markdown; convert defensively if a legacy post is still HTML).
  const diskBody = isHtmlContent(content) ? htmlToMarkdown(content) : content;
  const diskFm: PostFrontMatter = {
    site_slug: siteSlug,
    content_type: 'post',
    id: postId,
    title,
    slug,
    status,
    excerpt: seo.excerpt ?? '',
    tags: postTagsData?.data?.map((t) => t.name) ?? [],
    seo_title: seo.seo_title ?? '',
    meta_description: seo.seo_description ?? '',
    featured_image: featureImage.featured_image ?? '',
  };

  const handleOpenFromDisk = ({
    frontMatter,
    body,
  }: {
    frontMatter: PostFrontMatter;
    body: string;
  }) => {
    // Load a .md into the current editor as a NEW draft — the normal Save path owns
    // writing back to the server (front-matter id is surfaced so the user can choose
    // to overwrite). ponytail: tags aren't remapped name→id (no full tag list here);
    // re-tag via the selector before saving.
    setTitle(frontMatter.title);
    setSlug(frontMatter.slug);
    if (frontMatter.status === 'published' || frontMatter.status === 'archived') {
      setStatus(frontMatter.status);
    } else {
      setStatus('draft');
    }
    setContent(normalizeForEditor(body));
    setSeo((s) => ({
      ...s,
      seo_title: frontMatter.seo_title,
      seo_description: frontMatter.meta_description,
      excerpt: frontMatter.excerpt,
    }));
    setFeatureImage((f) => ({ ...f, featured_image: frontMatter.featured_image }));
    toast.info(`Loaded from disk (source id: ${frontMatter.id || 'none'})`);
  };

  return (
    <div className="@container">
      <UnsavedGuard dirty={dirty} noun="post" />
      {changedBar && (
        <BaxterChangedBar
          noun="post"
          onReload={() => void reloadFromServer()}
          onDismiss={() => setChangedBar(false)}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <h1 className="text-2xl font-semibold">Edit Post</h1>
        <div className="flex flex-wrap items-center gap-2">
          <EditorDiskBar fm={diskFm} body={diskBody} dirty={dirty} onOpen={handleOpenFromDisk} />
          <Button variant="secondary" onClick={handlePreview}>
            Preview
          </Button>
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()} className="grid min-w-0 gap-6 @min-[52rem]:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Content</Label>
            <PostEditor
              content={content}
              onChange={handleContentChange}
              siteSlug={siteSlug}
              folder="posts"
              storageType={siteData?.data?.storage_type}
            />
          </div>
        </div>

        <aside aria-label="Post settings" className="min-w-0 space-y-5 border-t pt-4 @min-[52rem]:border-t-0 @min-[52rem]:border-l @min-[52rem]:pt-0 @min-[52rem]:pl-6">
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="post-url-slug"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={status} onValueChange={(v: string) => setStatus(v as Post['status'])}>
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                {data.data?.status === 'scheduled' && <SelectItem value="scheduled">Scheduled</SelectItem>}
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            {status === 'scheduled' && <p role="status" className="text-sm text-muted-foreground">{scheduledPublicationMessage(status, data.meta?.scheduled_publication, data.data?.scheduled_for)}</p>}
          </div>

          <FeatureImageCard
            siteSlug={siteSlug}
            value={featureImage}
            onChange={(patch) => setFeatureImage((f) => ({ ...f, ...patch }))}
          />

          <div className="space-y-2">
            <TagSelector
              siteSlug={siteSlug}
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
            />
          </div>

          <PostSeoPanel value={seo} onChange={(patch) => setSeo((s) => ({ ...s, ...patch }))} />

          <GeoPanel
            content={content}
            excerpt={seo.excerpt}
            hasAuthor={!!data.data?.author_id}
            hasDate={!!data.data?.published_at || status === 'published'}
          />
        </aside>

        <div className="flex flex-wrap gap-2 @min-[52rem]:col-span-2">
          <SaveSplitButton
            onSave={handleSave}
            onSaveAndClose={handleSaveAndClose}
            isPending={updatePost.isPending}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate({ to: '/sites/$siteSlug/posts', params: { siteSlug } })}
          >
            Cancel
          </Button>
        </div>
      </form>
      <TranslationsPanel siteSlug={siteSlug} kind="post" sourceId={postId} />
      <TranslationWork siteSlug={siteSlug} kind="post" sourceId={postId} />

      {/* Distribution tracking */}
      <BarkPanel siteSlug={siteSlug} contentType="wpp_posts" contentId={postId} contentTitle={title} disabled={dirty} />

      {/* Fediverse engagement (shown when AP is enabled) */}
      <FediverseEngagement siteSlug={siteSlug} />
    </div>
  );
}

function FediverseEngagement({ siteSlug }: { siteSlug: string }) {
  const { data: apStatus } = useActivityPubStatus(siteSlug);
  if (!apStatus?.enabled) return null;

  return (
    <div className="border rounded p-4 mt-4">
      <h3 className="text-sm font-medium mb-2">Fediverse</h3>
      <div className="flex gap-4 text-sm text-muted-foreground">
        <span>{apStatus.follower_count} followers</span>
        <span>{apStatus.total_likes} likes</span>
        <span>{apStatus.total_boosts} boosts</span>
        <span>{apStatus.comment_count} replies</span>
      </div>
    </div>
  );
}
